// Common
import { ChangeDetectionStrategy, Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter, take } from 'rxjs';
import { Application } from '@modules/common/types/application';
import { CurrentUserStore } from '@frontendlib/common/services/current-user.store';
import { type DynamicPanelApp, DynamicPanelService } from '@modules/dynamic-panel/services/dynamic-panel.service';
import { atlasPreviousPanelAppKey, clearAtlasPreviousPanelApp, isAtlasPath, readAtlasPreviousPanelApp } from '@modules/dynamic-panel/services/atlas-previous-panel-app';
import { NotesWorkspaceContextService } from '@modules/notes/routing/notes-workspace-context.service';

/**
 * The Atlas screen's shell: a column that renders the lazy Atlas page into its outlet. No rail, no top bar
 * and no list — Atlas starts at y = 0, as Notes does.
 *
 * `standalone: false` for the same reason the sibling `*-layout` components are: it hosts
 * `app-collapsible-rail-shell`, which is module-declared. OnPush is a deliberate, standards-compliant
 * divergence from those older siblings (which set no change-detection strategy) — flagged for the
 * reviewer so it is not read as an inconsistency; the layout holds only static state, so OnPush is safe.
 *
 * Reuses `NotesWorkspaceContextService` (provided here, per component) so `/spaces/:spaceId/atlas` sets
 * the shared space context the same way `/spaces/:spaceId/notes` does; neutral-naming it for a third
 * app is an out-of-scope follow-up.
 *
 * Atlas is per person, like Mail, Notes and Boards: no team maps, no map switch. It imports nothing of the
 * lazy Atlas feature and makes no Atlas API request, so none of the lazy screen rides this eager shell.
 */
/** Whether this document was loaded by a reload of an /atlas address (the navigation entry's type and the address it was loaded at). */
function reloadedOnAtlas(): boolean {
  const entry = performance.getEntriesByType?.('navigation')?.[0] as PerformanceNavigationTiming | undefined;
  try {
    return entry?.type === 'reload' && isAtlasPath(new URL(entry.name).pathname);
  } catch {
    return false;
  }
}

@Component({
  selector: 'app-atlas-layout',
  templateUrl: './atlas-layout.component.html',
  styleUrls: ['./atlas-layout.component.less', '../../styles.less'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [NotesWorkspaceContextService],
})
export class AtlasLayoutComponent {
  private readonly workspaceContext = inject(NotesWorkspaceContextService);

  public constructor() {
    // Referenced so the per-component workspace context is read as a deliberate dependency, as before.
    void this.workspaceContext;
    // Entering Atlas opens the 4th panel on Atlas (4412 does; DECISIONS D-X6), by the call the panel's own dropdown makes.
    // Leaving Atlas gives the panel back to the app it had, unless the person chose another app while here (D-245).
    const panel = inject(DynamicPanelService);
    let previous: DynamicPanelApp;
    // A reload on Atlas forgets what the panel had: the earlier app is kept for this account in the tab (swept at sign-out, session-scoped-storage-keys.ts).
    const account = inject(CurrentUserStore).accountId();
    const key = atlasPreviousPanelAppKey(account);
    // Read and clear are the shared helper's (atlas-previous-panel-app.ts); only the write lives here.
    const keep = (value: string | null): void => {
      if (value === null) return clearAtlasPreviousPanelApp(account);
      try {
        if (key !== null) sessionStorage.setItem(key, value);
      } catch {
        // the tab keeps nothing: a reload on Atlas then leaves the panel on Atlas
      }
    };
    // `getApp()` answers `undefined` while permissions load (it must not mount an app on an unproven entitlement). A cold entry (address bar,
    // link) can land in that window: Atlas waits for the first resolved answer, so the panel's earlier app is read before Atlas overwrites it
    // (null = no app permitted, nothing to give back). Leaving first cancels the wait and Atlas never touches the panel (D-245).
    const destroyRef = inject(DestroyRef);
    let chosen = false;
    panel.getApp().pipe(filter((app) => app !== undefined), take(1), takeUntilDestroyed(destroyRef)).subscribe((first) => {
      previous = first;
      if (previous === Application.atlas || previous == null) {
        // What was kept is only for a reload ON Atlas (this document was loaded by a reload of an /atlas address); in any other document it is
        // a leftover of a full-page load away from Atlas, which never ran the cleanup below, and is dropped (MN-1).
        const earlier = reloadedOnAtlas() ? readAtlasPreviousPanelApp(account) : undefined;
        if (earlier !== undefined) previous = earlier;
        else keep(null);
      } else keep(previous);
      panel.setApp(Application.atlas);
      let onAtlas = false;
      panel.getApp().pipe(takeUntilDestroyed(destroyRef)).subscribe((app) => {
        if (app === Application.atlas) onAtlas = true;
        else if (onAtlas) {
          // The person chose another app while here (D-245): the choice is explicit from this moment, so the kept earlier app is dropped now, not at
          // destruction. A hard load of another address runs no destruction, and a surviving key would read as "Atlas was only set by entering it" (MD-09).
          chosen = true;
          keep(null);
        }
      });
    });
    destroyRef.onDestroy(() => {
      if (!chosen && previous != null && previous !== Application.atlas) panel.setApp(previous);
      keep(null);
    });
  }
}
